import React, { useContext, useMemo, useState } from 'react';

import { css, StyleSheet } from 'aphrodite';
import { Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { GLOBAL_PRODUCT_PDP, GLOBAL_PRODUCT_SUMMARY } from 'lib/networking/endpoints';
import {
  BG_1,
  BG_2,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  FONT_BODY,
  FONT_TITLE,
  PRIMARY_COLOR,
  PRIMARY_HOVER,
  SHADOW_XS,
  TOPBAR_HEIGHT,
} from 'components/upb_viewer_beta/css/betaTokens';
import { AuthContext } from 'components/providers/AuthContextProvider';
import BetaBreadcrumb from 'components/upb_viewer_beta/BetaBreadcrumb';
import BetaProductDetailsTab from 'components/upb_viewer_beta/pdp/BetaProductDetailsTab';
import BetaSalesPricingTab from 'components/upb_viewer_beta/pdp/BetaSalesPricingTab';
import BetaSegmentAnalysisTab from 'components/upb_viewer_beta/pdp/BetaSegmentAnalysisTab';
import BetaTaxonomyTab from 'components/upb_viewer_beta/pdp/BetaTaxonomyTab';
import BetaTopBar from 'components/upb_viewer_beta/BetaTopBar';
import { CUT_DRY_CLIENT_ID } from 'lib/constants';
import {
  DASH,
  fmtInt,
  fmtMoney,
  fmtPct,
  fmtPrice,
  fmtScore,
} from 'components/upb_viewer_beta/pdp/format';
import { getCategoryNameFromUrl } from 'components/upb_viewer/UPBViewer';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import SupportTicketsCreateModal from 'components/upb_viewer/SupportTicketsCreateModal';
import useGet from 'lib/hooks/useGet';
import useQueryString from 'lib/hooks/useQueryString';

const TABS = [
  { key: 'details', label: 'Product Details' },
  { key: 'sales', label: 'Sales & Pricing' },
  { key: 'segments', label: 'Segment Analysis' },
  { key: 'taxonomy', label: 'Taxonomy' },
  { key: 'similar', label: 'Similar Products' },
  { key: 'activity', label: 'Activity' },
];

// Step a header-strip value's font size down by length so long values fit the
// ~115px tile without overflow. `stage` is text, so it wraps once moderately
// long rather than tightening like a numeric value.
function pickStatValueSize(value, key) {
  const len = String(value == null ? '' : value).length;
  if (key === 'stage' && len >= 10) return styles.statValueWrap;
  if (len >= 14) return styles.statValueWrap;
  if (len >= 10) return styles.statValueTight;
  return null;
}

function ProductDetailsPage() {
  const { productId, productType } = useParams();
  const ref = useQueryString().get('ref');
  const { homeUrl } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('details');
  const [showIssueModal, setShowIssueModal] = useState(false);
  // Drives the BetaTopBar search box (same Cut+Dry/UPB top bar as the viewer).
  const [searchQuery, setSearchQuery] = useState('');

  // Full product (PDP). Also handed to the Product Details tab via `customData`
  // so the embedded PDP doesn't fetch it a second time.
  const { data: { data: product = {} } = {}, loading: productLoading } = useGet(
    GLOBAL_PRODUCT_PDP,
    { product_id: productId, product_type: productType, client_id: CUT_DRY_CLIENT_ID },
    !productId,
  );

  // Per-SKU summary for the header strip (GMV, market share, DPs, scores, stage, price).
  const { data: summary = {} } = useGet(`${GLOBAL_PRODUCT_SUMMARY}/${productId}`, {}, !productId);

  // Breadcrumb from the path we arrived through (the `ref`). Referrer segments
  // are already URL-encoded (they come from location.pathname), so they're used
  // as-is in the rebuilt URLs; getCategoryNameFromUrl decodes them for labels.
  const crumbs = useMemo(() => {
    const refParts = (ref ? ref.split('?')[0] : '').split('/').filter(Boolean);
    let result;
    if (refParts[0] === 'upb-mfr-viewer-beta') {
      // Arrived from the Manufacturer Sales Viewer (MSV): mirror its in-app path
      // Manufacturers › <manufacturer> › <brand> (no "Home"/category root).
      const base = refParts[0];
      const segs = refParts.slice(1); // [manufacturerSeg, brandSeg]
      result = [{ label: 'Manufacturers', url: `/${base}` }];
      segs.forEach((seg, idx) => {
        result.push({
          label: getCategoryNameFromUrl(seg) || (idx === 0 ? 'Manufacturer' : 'Brand'),
          url: `/${[base, ...segs.slice(0, idx + 1)].join('/')}`,
          tag: idx === 1 ? 'Brand' : undefined,
        });
      });
    } else if (refParts[0] === 'upb-viewer-beta' && refParts.length > 1) {
      // Arrived from the UPB Viewer Beta: Home › All Categories › L1 … › L4.
      const base = refParts[0];
      const segs = refParts.slice(1);
      result = [
        { label: 'Home', url: homeUrl || '/' },
        { label: 'All Categories', url: `/${base}` },
      ];
      segs.forEach((seg, idx) => {
        result.push({
          label: getCategoryNameFromUrl(seg) || 'Category',
          url: `/${[base, ...segs.slice(0, idx + 1)].join('/')}`,
          tag: `L${idx + 1}`,
        });
      });
    } else {
      result = [
        { label: 'Home', url: homeUrl || '/' },
        { label: 'All Categories', url: '/upb-viewer-beta' },
      ];
    }
    result.push({ label: product.name || 'Product' });
    return result;
  }, [ref, homeUrl, product.name]);

  // L30D / annualized summary strip. Metrics with no per-SKU source yet render "-"
  // (DPs, units and pricing land with the Sales & Pricing PR).
  const summaryMetrics = useMemo(
    () => [
      { key: 'stage', label: 'SKU Stage', value: summary.sku_stage || DASH },
      { key: 'gmv', label: 'Annualized GMV', value: fmtMoney(summary.annualized_gmv) },
      { key: 'share', label: 'L4 Market Share', value: fmtPct(summary.l4_market_share) },
      { key: 'dps', label: 'DPs', value: fmtInt(summary.dp_count) },
      // Labels kept short so each fits one line; a wrapped label would stretch
      // every tile taller via `alignItems: stretch`.
      { key: 'units', label: 'Annualized Units', value: fmtInt(summary.annualized_units) },
      { key: 'median', label: 'Median Price', value: fmtPrice(summary.median_selling_price) },
      { key: 'selling', label: 'Avg Selling Price', value: fmtPrice(summary.avg_selling_price) },
      { key: 'ecom', label: 'eCom Score', value: fmtScore(summary.p0_score) },
      { key: 'att', label: 'Att. Score', value: fmtScore(summary.fsa_score) },
    ],
    [summary],
  );

  // The reporter is already looking at the product, so the ticket modal doesn't
  // need to ask for a distributor and can take the manufacturer from the PDP.
  const ticketManufacturer = useMemo(
    () =>
      product?.manufacturer?.id
        ? { label: product.manufacturer.name, value: product.manufacturer.id }
        : null,
    [product?.manufacturer],
  );

  if (productLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={css(styles.page)}>
      <BetaTopBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      <div className={css(styles.content)}>
        <BetaBreadcrumb crumbs={crumbs} />

        <div className={css(styles.headerCard)}>
          <h1 className={css(styles.title)}>{product.name || 'Product'}</h1>
          <div className={css(styles.statsBox)}>
            {summaryMetrics.map((m, i) => (
              <div
                key={m.key}
                className={css(
                  styles.statCell,
                  i === summaryMetrics.length - 1 && styles.statCellLast,
                )}
              >
                <span className={css(styles.statLabel)}>{m.label}</span>
                <span className={css(styles.statValue, pickStatValueSize(m.value, m.key))}>
                  {m.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className={css(styles.tabsBar)}>
          {TABS.map(t => (
            <Button
              key={t.key}
              type="button"
              variant="link"
              className={css(styles.tab, activeTab === t.key && styles.tabActive)}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </Button>
          ))}
          <Button
            type="button"
            variant="link"
            className={css(styles.reportIssue)}
            onClick={() => setShowIssueModal(true)}
          >
            <span aria-hidden>⚠</span>
            Report a Data Issue
          </Button>
        </div>

        <div className={css(styles.pane)}>
          {activeTab === 'details' && <BetaProductDetailsTab product={product} />}
          {activeTab === 'sales' && <BetaSalesPricingTab productId={productId} />}
          {activeTab === 'segments' && <BetaSegmentAnalysisTab product={product} />}
          {activeTab === 'taxonomy' && <BetaTaxonomyTab product={product} productId={productId} />}
          {!['details', 'sales', 'segments', 'taxonomy'].includes(activeTab) && (
            <div className={css(styles.placeholder)}>
              {TABS.find(t => t.key === activeTab)?.label} — coming soon.
            </div>
          )}
        </div>
      </div>

      {showIssueModal && (
        <SupportTicketsCreateModal
          onHide={() => setShowIssueModal(false)}
          refetch={() => {}}
          product={product}
          typeOfProduct={productType}
          hideDistributorSelect
          presetManufacturer={ticketManufacturer}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    fontFamily: FONT_BODY,
    backgroundColor: BG_2,
    minHeight: '100%',
    // Clear the fixed BetaTopBar so content starts below it.
    paddingTop: TOPBAR_HEIGHT,
  },
  content: {
    maxWidth: 1440,
    margin: '0 auto',
    padding: '18px 28px 60px',
  },
  headerCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderBottom: 'none',
    borderRadius: '12px 12px 0 0',
    boxShadow: SHADOW_XS,
    padding: '18px 22px',
  },
  title: {
    fontFamily: FONT_TITLE,
    fontSize: 26,
    lineHeight: 1.2,
    fontWeight: 800,
    letterSpacing: '-0.01em',
    color: FG_1,
    margin: 0,
  },
  // Flex+wrap with per-tile right borders as dividers (a grid-gap divider
  // painted a wide band whenever a tile orphaned to the next row).
  statsBox: {
    display: 'flex',
    alignItems: 'stretch',
    flexWrap: 'wrap',
    gap: 0,
    flexShrink: 1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: 10,
    overflow: 'hidden',
    // Parent headerCard is `alignItems: flex-start`; without these the strip
    // would shrink to content width and collapse into one column.
    width: '100%',
    alignSelf: 'stretch',
  },
  statCell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 2,
    padding: '10px 16px',
    backgroundColor: BG_1,
    borderRight: `1px solid ${BORDER_2}`,
    // Cells stretch to fill the row; the 220px cap stops a wrapped orphan tile
    // from blowing up to span the whole row.
    minWidth: 108,
    maxWidth: 220,
    flex: '1 1 108px',
  },
  statCellLast: {
    borderRight: 'none',
  },
  statLabel: {
    fontFamily: FONT_BODY,
    fontSize: 11,
    fontWeight: 600,
    color: FG_3,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    lineHeight: 1.25,
  },
  statValue: {
    fontFamily: FONT_TITLE,
    // Base size; long values step down via `pickStatValueSize`.
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: '-0.01em',
    color: FG_1,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Numeric values that overflow at 18px; tighten but keep nowrap.
  statValueTight: {
    fontSize: 16,
  },
  // Long text values; shrink and allow wrapping to two lines.
  statValueWrap: {
    fontSize: 13,
    lineHeight: 1.3,
    whiteSpace: 'normal',
    textOverflow: 'clip',
  },
  tabsBar: {
    display: 'flex',
    alignItems: 'stretch',
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderTop: 'none',
    borderRadius: '0 0 12px 12px',
    padding: '0 16px',
  },
  tab: {
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    marginBottom: -1,
    padding: '14px 18px',
    fontFamily: FONT_BODY,
    fontSize: 14,
    fontWeight: 700,
    color: FG_3,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    ':hover': {
      color: FG_1,
    },
  },
  tabActive: {
    color: PRIMARY_COLOR,
    borderBottomColor: PRIMARY_COLOR,
  },
  reportIssue: {
    marginLeft: 'auto',
    alignSelf: 'center',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: FONT_BODY,
    fontSize: 13,
    fontWeight: 700,
    color: FG_2,
    padding: '7px 14px',
    borderRadius: 9999,
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    boxShadow: SHADOW_XS,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    ':hover': {
      color: PRIMARY_HOVER,
      borderColor: PRIMARY_COLOR,
    },
  },
  pane: {
    marginTop: 20,
  },
  placeholder: {
    backgroundColor: BG_1,
    border: `1px dashed ${BORDER_2}`,
    borderRadius: 12,
    color: FG_3,
    fontSize: 15,
    padding: 24,
    textAlign: 'center',
  },
});

export default ProductDetailsPage;
